import React, {ReactNode, useState} from 'react';
import {Dialog} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {formatMinutes} from './record-math';
import {crewInitials} from './CrewCard';
import {TimerPill} from './WorkItemEdit';
import {
  estimatedFinishTime, FinishJobCheck, formatRunningClock, formatScheduledDate, formatScheduledTime,
  jobWallMinutes, secondsToMinutes, steppedCrewSeconds, toIsoDate,
} from './job-math';
import './JobRecord.css';

const crewCaption = (n: number) => `Calculating off of ${n} crew member${n === 1 ? '' : 's'}`;

/*
 * The job's own pieces (SQ-597), Android's: the SCHEDULED card and the
 * Schedule popup (JobSchedule.kt), the crew clocks (JobTimerView.kt), the
 * finishing cards (JobReviewViews.kt) and the completed job's readouts
 * (ActualTimeReadout.kt). The wide-page rules are SQ-596's: the cards fill
 * the record column, buttons and steppers keep their phone size, centred.
 */

// ---- SCHEDULED (ScheduledSummaryCard) ----------------------------------------------------------

export const ScheduledCard = ({date, time, onSiteMinutes, onEdit}: {
  date?: string | null; time?: string | null; onSiteMinutes?: number; onEdit?: () => void;
}) => {
  const day = formatScheduledDate(date);
  const start = formatScheduledTime(time);
  const finish = estimatedFinishTime(time, onSiteMinutes);
  return (
    <div className="jb-scheduled" role="group" aria-label="Scheduled">
      <div className="jb-scheduled-head">
        <span>SCHEDULED</span>
        {onEdit && <button type="button" className="jb-link" onClick={onEdit}>Edit</button>}
      </div>
      {day && <div className="jb-scheduled-line"><span>Date</span><b>{day}</b></div>}
      {start && <div className="jb-scheduled-line"><span>Time</span><b>{finish ? `${start} – ${finish}` : start}</b></div>}
    </div>
  );
};

// ---- The Schedule popup (ScheduleJobDialog) --------------------------------------------------------

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** The month calendar: no past days, and no going back before this month. */
const CalendarPicker = ({value, onChange}: {value?: string; onChange: (iso: string) => void}) => {
  const today = startOfDay(new Date());
  const picked = value ? new Date(`${value}T00:00:00`) : undefined;
  const [month, setMonth] = useState(() => new Date((picked ?? today).getFullYear(), (picked ?? today).getMonth(), 1));
  const first = month.getDay();
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const atThisMonth = month.getFullYear() === today.getFullYear() && month.getMonth() === today.getMonth();
  const cells = [...Array(first).fill(null), ...Array.from({length: days}, (_, i) => i + 1)];
  return (
    <div className="jb-calendar">
      <div className="jb-calendar-head">
        <button type="button" aria-label="Previous month" disabled={atThisMonth}
                onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}><ChevronLeftIcon/></button>
        <span>{MONTHS[month.getMonth()]} {month.getFullYear()}</span>
        <button type="button" aria-label="Next month" onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}><ChevronRightIcon/></button>
      </div>
      <div className="jb-calendar-grid">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <span key={i} className="jb-calendar-dow">{d}</span>)}
        {cells.map((day, i) => {
          if (day === null) return <span key={i}/>;
          const date = new Date(month.getFullYear(), month.getMonth(), day);
          const iso = toIsoDate(date);
          const past = date < today;
          const classes = ['jb-day', iso === value && 'jb-day--on', date.getTime() === today.getTime() && 'jb-day--today'].filter(Boolean).join(' ');
          return <button type="button" key={i} className={classes} disabled={past} onClick={() => onChange(iso)} aria-pressed={iso === value}>{day}</button>;
        })}
      </div>
    </div>
  );
};

/** The analog clock: an hour, then the minutes; AM / PM beside the readout. Every tap updates the draft. */
const ClockPicker = ({value, onChange}: {value?: string; onChange: (hhmm: string) => void}) => {
  const [h24, m] = (value ?? '08:00').split(':').map(Number);
  const [stage, setStage] = useState<'hour' | 'minute'>('hour');
  const pm = h24 >= 12;
  const h12 = h24 % 12 || 12;
  const set = (hour12: number, minute: number, afternoon: boolean) =>
    onChange(`${String((hour12 % 12) + (afternoon ? 12 : 0)).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
  const marks = stage === 'hour' ? Array.from({length: 12}, (_, i) => i + 1) : Array.from({length: 12}, (_, i) => i * 5);
  const current = stage === 'hour' ? h12 : m;
  // The hand points at the current value, a mark or between two (a minute not on a five).
  const handTurns = stage === 'hour' ? (h12 % 12) / 12 : m / 60;
  return (
    <div className="jb-clock">
      <div className="jb-clock-readout">
        <button type="button" className={stage === 'hour' ? 'jb-clock-part jb-clock-part--on' : 'jb-clock-part'} onClick={() => setStage('hour')}>{String(h12).padStart(2, '0')}</button>
        <span>:</span>
        <button type="button" className={stage === 'minute' ? 'jb-clock-part jb-clock-part--on' : 'jb-clock-part'} onClick={() => setStage('minute')}>{String(m).padStart(2, '0')}</button>
        <span className="jb-ampm">
          <button type="button" className={!pm ? 'jb-ampm-on' : undefined} onClick={() => set(h12, m, false)}>AM</button>
          <button type="button" className={pm ? 'jb-ampm-on' : undefined} onClick={() => set(h12, m, true)}>PM</button>
        </span>
      </div>
      <div className="jb-clock-face">
        {marks.map((mark, i) => {
          const angle = (i / 12) * 2 * Math.PI - Math.PI / 2 + (stage === 'hour' ? 2 * Math.PI / 12 : 0);
          const on = stage === 'hour' ? mark === current : mark === current;
          return (
            <button type="button" key={mark} className={on ? 'jb-clock-mark jb-clock-mark--on' : 'jb-clock-mark'}
                    style={{left: `${50 + 40 * Math.cos(angle)}%`, top: `${50 + 40 * Math.sin(angle)}%`}}
                    aria-label={stage === 'hour' ? `${mark} o'clock` : `${mark} minutes`}
                    onClick={() => {
                      if (stage === 'hour') { set(mark, m, pm); setStage('minute'); } else set(h12, mark, pm);
                    }}>
              {stage === 'hour' ? mark : String(mark).padStart(2, '0')}
            </button>
          );
        })}
        <span className="jb-clock-hand" style={{transform: `rotate(${handTurns * 360 - 90}deg)`}}/>
        <span className="jb-clock-centre"/>
      </div>
    </div>
  );
};

/**
 * Schedule This Job: a draft seeded from the saved schedule each time it
 * opens; only Save commits it. Cancel Schedule clears the saved schedule.
 */
export const ScheduleDialog = ({date, time, onSave, onCancelSchedule, onClose}: {
  date?: string | null; time?: string | null; onSave: (date: string | null, time: string | null) => void; onCancelSchedule: () => void; onClose: () => void;
}) => {
  const [draftDate, setDraftDate] = useState(date ?? undefined);
  const [draftTime, setDraftTime] = useState(time ?? undefined);
  const [view, setView] = useState<'main' | 'date' | 'time'>('main');
  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{className: 'jb-dialog'}}>
      {view === 'main' && <>
        <div className="jb-dialog-title">Schedule This Job</div>
        <div className="jb-drafts">
          <button type="button" className="jb-draft" onClick={() => setView('date')}>
            <DateRangeIcon/>{formatScheduledDate(draftDate) ?? 'Select Date'}
          </button>
          <button type="button" className="jb-draft" onClick={() => { setDraftTime(t => t ?? '08:00'); setView('time'); }}>
            <AccessTimeIcon/>{formatScheduledTime(draftTime) ?? 'Select Time'}
          </button>
        </div>
        <button type="button" className="jb-dialog-button jb-dialog-button--outlined-red" onClick={() => { onClose(); onCancelSchedule(); }}>Cancel Schedule</button>
        <button type="button" className="jb-dialog-button" onClick={() => { onClose(); onSave(draftDate ?? null, draftTime ?? null); }}>Save</button>
      </>}
      {view === 'date' && <>
        <div className="jb-dialog-title">Select Date</div>
        <CalendarPicker value={draftDate} onChange={setDraftDate}/>
        <button type="button" className="jb-dialog-button" disabled={!draftDate} onClick={() => setView('main')}>Set Date</button>
      </>}
      {view === 'time' && <>
        <div className="jb-dialog-title">Select Time</div>
        <ClockPicker value={draftTime} onChange={setDraftTime}/>
        <button type="button" className="jb-dialog-button" onClick={() => setView('main')}>Set Time</button>
      </>}
    </Dialog>
  );
};

// ---- Crew clocks (CrewJobTimerList, JobTimeHeader, TrackedJobTimeSection) -------------------------

export interface CrewClock {
  id: number;
  name: string;
  seconds: number;
  running: boolean;
  /** SQ-745: whether this person may start, stop and correct this clock (every clock, or only their own). */
  controllable?: boolean;
}

/** PlayStopButton: a green circle with a white triangle; running, red with a white square. */
const PlayStop = ({running, name, disabled, onClick}: {running: boolean; name: string; disabled?: boolean; onClick: () => void}) => (
  <button type="button" className={running ? 'jb-play jb-play--running' : 'jb-play'} onClick={onClick} disabled={disabled}
          aria-label={running ? `Stop Job Time, ${name}` : `Start Job Time, ${name}`}>
    <span className={running ? 'jb-play-stop' : 'jb-play-start'}/>
  </button>
);

const CrewLine = ({member, controls, disabled, onToggle, onSetSeconds}: {
  member: CrewClock; controls?: 'clock' | 'pencil'; disabled?: boolean; onToggle: () => void; onSetSeconds: (seconds: number) => void;
}) => {
  const [editing, setEditing] = useState(false);
  return (
    <div className="jb-crew-line">
      <div className="jb-crew-line-row">
        <span className="er-avatar" style={{width: 28, height: 28, fontSize: 11}}>{crewInitials(member.name)}</span>
        <span className="jb-crew-line-name">{member.name}</span>
        <span className={member.running ? 'jb-crew-line-time jb-crew-line-time--running' : 'jb-crew-line-time'}>
          {member.running ? formatRunningClock(member.seconds) : formatMinutes(secondsToMinutes(member.seconds))}
        </span>
        {controls === 'clock' && <PlayStop running={member.running} name={member.name} disabled={disabled} onClick={onToggle}/>}
        {controls && (
          <button type="button" className="jb-pencil" disabled={disabled} onClick={() => setEditing(e => !e)} aria-label={`Edit Job Time, ${member.name}`}
                  aria-expanded={editing}><EditIcon/></button>
        )}
      </div>
      {editing && controls && (
        <div className="jb-stepper">
          <TimerPill target={`Job Time, ${member.name}`} onStep={delta => onSetSeconds(steppedCrewSeconds(member.seconds, delta))}/>
          <button type="button" className="jb-done" onClick={() => setEditing(false)}>Done</button>
        </div>
      )}
    </div>
  );
};

/** "JOB TIME  8h 25m · avg of 2 crew", then a line per crew member. */
export const JobTimeList = ({crew, controls, disabled, emptyText, onToggle, onSetSeconds}: {
  crew: CrewClock[]; controls?: 'clock' | 'pencil'; disabled?: boolean; emptyText?: ReactNode;
  onToggle: (id: number) => void; onSetSeconds: (id: number, seconds: number) => void;
}) => (
  <div className="jb-job-time">
    <div className="jb-job-time-head">
      <span className="jb-job-time-caption">JOB TIME</span>
      {crew.length > 0 && <span className="jb-job-time-value">{formatMinutes(jobWallMinutes(crew.map(m => m.seconds)))}</span>}
      {crew.length > 1 && <span className="jb-job-time-avg"> · avg of {crew.length} crew</span>}
    </div>
    {crew.length === 0 && emptyText}
    {crew.map(member => (
      <CrewLine key={member.id} member={member} controls={member.controllable === false ? undefined : controls} disabled={disabled}
                onToggle={() => onToggle(member.id)} onSetSeconds={s => onSetSeconds(member.id, s)}/>
    ))}
  </div>
);

// ---- Finishing (JobReviewViews.kt) ------------------------------------------------------------

const TanCard = ({children, className, label}: {children: ReactNode; className?: string; label?: string}) =>
  <div className={className ? `jb-tan ${className}` : 'jb-tan'} role="group" aria-label={label}>{children}</div>;

/** ReviewJobBanner: above the crew while the job is reviewed. */
export const ReviewJobBanner = () => (
  <TanCard label="Review the job">
    <div className="jb-title jb-title--banner">Check the job before you finish it</div>
    <p>Make sure the work that was planned is still accurate. If you need to make any changes, tap Edit, make those changes and then save them.</p>
    <p>Any adjustments needed to the crew hours, the work plan and equipment can be made without tapping Edit.</p>
    <p>When everything looks right, finish the job at the bottom.</p>
  </TanCard>
);

/** TimeAllocationCard: under a work item's total while the job is reviewed. */
export const TimeAllocationCard = ({wallMinutes, crew, canMove, adjusted, target, onStep, onResetAll}: {
  wallMinutes: number; crew: number; canMove: boolean; adjusted: boolean; target: string;
  onStep: (deltaWall: number) => void; onResetAll: () => void;
}) => (
  <TanCard className="jb-allocation" label={`Allocated time, ${target}`}>
    <div className="jb-allocation-top"><span>Estimated to have taken</span><b>{formatMinutes(wallMinutes)}</b></div>
    {canMove && <>
      <TimerPill target={`Allocated time, ${target}`} caption={crewCaption(crew)} onStep={onStep}/>
      <div className="jb-explain">You can adjust this. Your total job time will stay the same; this just moves time to and from other parts of the job.</div>
    </>}
    {adjusted && <button type="button" className="jb-reset" onClick={onResetAll}>Reset All Times</button>}
  </TanCard>
);

/** FinishJobFooter: the button, and under it "Ready to finish the job?". */
export const FinishJobFooter = ({check, disabled, onReview}: {check: () => FinishJobCheck; disabled?: boolean; onReview: () => void}) => {
  const [open, setOpen] = useState(false);
  const [blockers, setBlockers] = useState<string[]>([]);
  const warnings = open ? check().warnings : [];
  return (
    <div className="er-footer-stack">
      <button type="button" className="er-filled" disabled={disabled} onClick={() => { setOpen(o => !o); setBlockers([]); }}>Finish the Job</button>
      {open && (
        <TanCard className="jb-finish" label="Ready to finish the job?">
          <div className="jb-title">Ready to finish the job?</div>
          <div className="jb-text">This will stop the timers and let you check the work.</div>
          {warnings.length > 0 && <>
            <div className="jb-worth">Worth a look:</div>
            {warnings.map(line => <div key={line} className="jb-warning">• {line}</div>)}
          </>}
          {blockers.length > 0 && <>
            <div className="jb-rule"/>
            {blockers.map(line => <div key={line} className="jb-blocker" role="alert">{line}</div>)}
          </>}
          <div className="jb-buttons">
            <button type="button" className="er-tonal er-tonal--compact jb-not-yet" onClick={() => { setOpen(false); setBlockers([]); }}>Not Yet</button>
            <button type="button" className="er-filled jb-yes" disabled={disabled} onClick={() => {
              const now = check();
              if (now.blockers.length > 0) { setBlockers(now.blockers); return; }
              setOpen(false);
              onReview();
            }}>Yes, Review Job Details</button>
          </div>
        </TanCard>
      )}
    </div>
  );
};

/** ReviewFinishFooter: the bottom of a job in review. */
export const ReviewFinishFooter = ({disabled, onFinish, onGoBack}: {disabled?: boolean; onFinish: () => void; onGoBack: () => void}) => (
  <TanCard className="jb-finish" label="Everything look right?">
    <div className="jb-title">Everything look right?</div>
    <div className="jb-text jb-text--gap">Finishing moves the job to Completed and shows how the bid held up.</div>
    <div className="jb-stack">
      <button type="button" className="er-filled" disabled={disabled} onClick={onFinish}>Finish and Move Job to Completed</button>
      <button type="button" className="er-tonal" disabled={disabled} onClick={onGoBack}>Go Back to Job</button>
    </div>
  </TanCard>
);
